/**
 * "Price a capital job, start to finish" — a whole capital bid, in Project Financials' step layout.
 *
 * The owner, 1 Oct 2026: "build the show me how". The day before, looking at Capex on staging: "the
 * pages seem out of order and cluttered together" — so 2.110.0 put the estimate on ONE numbered step
 * bar, and this tour walks it, in that order, on the real pages. Since the bill of materials (2026-10-03)
 * the bar is 1 Job details → 2 Scope & take-off → 3 Bill of materials → 4 Labour & crews → 5 Schedule →
 * 6 Indirects → 7 Price & terms → 8 Proposal → 9 Signature → 10 Award → 11 Project (`shell/nav.tsx`
 * ESTIMATE_PAGES); the cards' step numbers are that bar's (C4, audit A4: from step 3 on every number was one
 * too low, and there was no Bill of materials step).
 *
 * ── A PRACTICE RUN ON A PRACTICE ESTIMATE ───────────────────────────────────────────────────────
 * Every other tour makes something NEW from a real screen, so blocking its writes is the whole of
 * "nothing left behind". This one cannot: what it teaches is how an estimate that already has systems,
 * crews, a schedule and a price is worked through to an award, and awarding a real one writes a
 * project, a frozen baseline and a job with a budget. So it walks a PRACTICE ESTIMATE that exists only
 * inside the browser for the length of the run — see practice/capexEstimate.js for what it is, why it is
 * not a seeded database row, and why it can never answer for a real estimate. The pages, the buttons
 * and the clicks are all real; the estimate, the PDF, the signature request, the award and the job are
 * not, and none of them exists after the run.
 *
 * ── GUIDED, NOT A SLIDESHOW ─────────────────────────────────────────────────────────────────────
 * Every step that asks for something waits for EVIDENCE of it, never for a click on Next:
 *
 *   opening a page   `appears` on that page's own head — so the step bar, the page's Next button or a
 *                    step opened early all count, and nobody is told to open a page they are on;
 *   saving the name  `requested` on the header save — Enter on an unchanged name sends nothing, and
 *                    the card waits;
 *   If left out      `appears` on the re-priced sentence the server's answer draws;
 *   the PDF          `requested` on the generate — the press, not the button;
 *   the signature    `requested` on the request — and it is anchored on the WHOLE signing panel, because
 *                    the signer picker opens inside it and a spotlight on the button would dim the picker;
 *   the award        `appears` on the award's own questions, then `requested` on the award itself.
 *
 * Reading is never gated: a card that only explains keeps Next.
 *
 * ── WHO IS OFFERED WHAT ─────────────────────────────────────────────────────────────────────────
 * The tour stands on reading capital estimates (View Bids) with Project Financials AND Capital
 * Projects on — the estimate's routes answer only with both (CapexRoute.jsx, BLOCKER 2). Each step that
 * needs more names it, and a step somebody cannot do is REMOVED, never shown greyed:
 *
 *   naming the job               Edit Bids
 *   the PDF, the Signature step  Export Bids (the documents' own gate); asking for a signature also
 *                                Edit Bids (SigningPanel)
 *   the award and the project    Edit Bids + Create Projects + View Project Financials (the award's right,
 *                                PLAN-28 D12 as C4 audit F12 left it; the set-up and the cost report ask
 *                                the same). Somebody without them reads the Award page and is told who can.
 *
 * The permission is the SAME list on every award and project step so they stand or fall together: a
 * reader who could press Award but not see the project it opens would be walked to a step that cannot
 * appear.
 *
 * ── WHERE IT ENDS ───────────────────────────────────────────────────────────────────────────────
 * On the top nav, which is inert and on every page the reader can be on by then — the project's cost
 * report for somebody who awarded, the Award page for somebody who could not. Ending on the award
 * (a write) would end the run, and switch practice mode off, at the instant that write is answered.
 */
import { defineTour, AUDIENCE, ADVANCE_ON } from '../tourSchema'
import { CAPEX_TOUR_ID, PRACTICE_JOB_NUMBER, practiceEstimatePath } from '../practice/capexEstimate'

const BOARD = '/capex/projects'
const OVERVIEW = practiceEstimatePath()
const page = (segment) => practiceEstimatePath(segment)
/** The practice job's cost report, for the anchor guard. Its real address carries the number the award was given. */
const PROJECT_SCREEN = `/capex/projects/${PRACTICE_JOB_NUMBER}/cost`

const AWARD_AND_SET_UP = ['bid:edit_bid', 'project:create', 'project:view_financial']

/** "Open step N" — the same card shape for every page, gated on that page actually opening. */
const openStep = (id, segment, from, title, body, extra = {}) => ({
  id,
  anchor: `capex.estimate.step.${segment}`,
  // No `route`: the step bar is on every page of the estimate, and a route here would let the engine
  // "helpfully" send back somebody who had already moved on with the page's own Next button.
  screen: from,
  title,
  body,
  advanceOn: ADVANCE_ON.DO,
  until: { appears: `capex.estimate.page.${segment}` },
  ...extra,
})

export default defineTour({
  id: CAPEX_TOUR_ID,
  title: 'Price a capital job, start to finish',
  summary: 'A whole capital bid on a practice estimate: scope, labour, schedule, price, proposal, signature, award and the project it becomes. Nothing is saved.',
  audience: AUDIENCE.ONBOARDING,
  module: 'capex',
  requiresSwitch: 'capital',
  permission: 'bid:view_bids',
  // A RESUMED run (after a reload or a break) starts the practice estimate afresh — it lived in the
  // browser — so a step that stands on an earlier step's work goes back to that step (`resumeFrom`), and
  // the first card says so. Without this the PDF or the award was simply not there and the step that
  // needed it was skipped in silence (review of 17f785bc, F3).
  resumeNote: 'The practice estimate starts fresh after a reload or a break, so the walkthrough has gone back to the step that makes what comes next.',

  steps: [
    {
      id: 'open-practice-estimate',
      anchor: 'capex.board.practice-estimate',
      route: BOARD,
      screen: `${BOARD}#practice`,
      title: 'Open the practice estimate',
      body: 'Every capital job starts here, on the Pipeline: estimates being built, bids out with customers, jobs under contract. Up top is always the same — Pipeline, Company reports, Settings. For this walkthrough there is a practice estimate on the list. Open it. Nothing you do in it is saved.',
      advanceOn: ADVANCE_ON.DO,
      until: { appears: 'capex.estimate.page.overview' },
      stuckHint: 'No practice estimate on the list? It is only there while this walkthrough runs. If the list says you may not see estimates, ask an admin for View Bids.',
    },
    {
      id: 'what-an-estimate-is',
      anchor: 'capex.estimate.steps',
      route: OVERVIEW,
      title: 'An estimate is the price before you win it',
      body: 'It is built in eleven steps, in the order the work is done: what the job is, its scope, the bill of materials, the crews, the schedule, the indirects, the price, the proposal, the signature, the award — and then the project it becomes. A tick means a step is done. Back and Next at the foot of every page walk them in order.',
      advanceOn: ADVANCE_ON.NEXT,
    },
    {
      id: 'name-the-job',
      anchor: 'capex.estimate.job-details',
      route: OVERVIEW,
      permission: 'bid:edit_bid',
      title: 'Step 1 · Job details',
      body: 'Who the job is for and what it is called — nothing here moves the price. Change the Job name to anything you like and press Enter to save it. Watch the title above change.',
      advanceOn: ADVANCE_ON.DO,
      until: { requested: 'PATCH /header' },
      stuckHint: 'Nothing has been sent yet: a save only goes when something changed. Type a different name in Job name, then press Enter.',
    },
    openStep('open-scope', 'scope', OVERVIEW, 'Step 2 · Scope & take-off',
      'Open Scope & take-off on the step bar.'),
    {
      id: 'systems',
      anchor: 'capex.estimate.systems',
      route: page('scope'),
      title: 'Systems: the parts the customer would recognise',
      body: 'Process piping, pipe supports. Each system becomes its own cost code if you win the job, so cost can be measured against it later. Base is in the price; Alternate and Held are priced but kept apart.',
      advanceOn: ADVANCE_ON.NEXT,
    },
    {
      id: 'leave-a-system-out',
      anchor: 'capex.estimate.system.if-left-out',
      route: page('scope'),
      title: 'What is one system worth?',
      body: 'Press If left out. The estimate is re-priced without that system, and you see what really comes off the price — and how much of the job’s indirect cost stays whatever happens to it.',
      advanceOn: ADVANCE_ON.DO,
      until: { appears: 'capex.estimate.system.leaving-it-out' },
    },
    {
      id: 'take-off',
      anchor: 'capex.estimate.takeoff',
      route: page('scope'),
      title: 'Take-off lines start from the work',
      body: 'Each line starts by PICKING the work it is — run 6-inch pipe, a butt weld — from your Rate library, never by typing it. The pick brings the unit and the hours per unit (learned from your finished jobs where there are enough, published otherwise); you give the quantity from the drawings — 400 feet, 60 welds. That is the hours the scope calls for. The Rate library itself is under Company reports.',
      advanceOn: ADVANCE_ON.NEXT,
    },
    openStep('open-bom', 'bom', page('scope'), 'Step 3 · Bill of materials',
      'Open Bill of materials.'),
    {
      id: 'the-bill',
      anchor: 'capex.estimate.page.bom',
      route: page('materials'),
      title: 'What the job buys, from the take-off',
      body: 'The bill is made from what each take-off line buys, grouped by what it is. Send it to vendors for quotes, compare them, and use one quote as the material cost. Until a quote is used, the bill prices nothing.',
      advanceOn: ADVANCE_ON.NEXT,
    },
    openStep('open-labour', 'labour', page('materials'), 'Step 4 · Labour & crews',
      'Open Labour & crews.'),
    {
      id: 'the-crew',
      anchor: 'capex.estimate.crews',
      route: page('labour'),
      title: 'A crew is who does the work',
      body: 'Two fitters, a welder and a helper, each picked from your rates — a trade is picked, never typed. The crew’s blended rate is what an hour of this crew costs the customer.',
      advanceOn: ADVANCE_ON.NEXT,
    },
    {
      id: 'a-work-item',
      anchor: 'capex.estimate.work-items',
      route: page('labour'),
      title: 'A work item is what the price of labour is made of',
      body: 'Quantity × hours per unit × the crew’s rate. “Run 6-inch pipe”: 400 feet at half an hour a foot is 200 hours of the pipe crew. Each one sits under a system and can be tied to its take-off line.',
      advanceOn: ADVANCE_ON.NEXT,
    },
    openStep('open-schedule', 'schedule', page('labour'), 'Step 5 · Schedule',
      'Open Schedule.'),
    {
      id: 'the-schedule',
      anchor: 'capex.estimate.schedule-picture',
      route: page('schedule'),
      title: 'When the work happens',
      body: 'Each dated work item is a bar, with the man-hours a day underneath. The schedule sets the job’s length — and supervision and per diem are priced from that length, which is why Schedule comes before Indirects.',
      advanceOn: ADVANCE_ON.NEXT,
    },
    openStep('open-indirects', 'indirects', page('schedule'), 'Step 6 · Indirects',
      'Open Indirects.'),
    {
      id: 'the-indirects',
      anchor: 'capex.estimate.supervision',
      route: page('indirects'),
      title: 'What the job needs that is not on the tools',
      body: 'Supervision, equipment, per diem and other direct costs. They are bought for the whole job, so leaving one system out does not make them smaller — that was the “stays” figure on Scope.',
      advanceOn: ADVANCE_ON.NEXT,
    },
    openStep('open-price', 'price', page('indirects'), 'Step 7 · Price & terms',
      'Open Price & terms.'),
    {
      id: 'the-build-up',
      anchor: 'capex.estimate.waterfall',
      route: page('price'),
      title: 'How the price is built',
      body: 'In the engine’s order: labour, materials, the indirects, a subtotal, then contingency and the bond on top. Contingency is cost kept for what goes wrong — it is not profit. The bottom line is what the customer is quoted.',
      advanceOn: ADVANCE_ON.NEXT,
    },
    openStep('open-proposal', 'proposal', page('price'), 'Step 8 · Proposal',
      'Open Proposal.'),
    {
      id: 'make-the-pdf',
      anchor: 'capex.estimate.proposal.create-pdf',
      route: page('proposal'),
      permission: 'bid:export_bid',
      title: 'Make the PDF',
      body: 'The proposal is the letter the customer signs: the words above, the price from step 7. Create PDF files a numbered copy, and that copy is what gets signed. Press it — on practice, nothing is filed.',
      advanceOn: ADVANCE_ON.DO,
      until: { requested: 'POST /documents/generate' },
    },
    openStep('open-signature', 'signature', page('proposal'), 'Step 9 · Signature',
      'Open Signature.', { permission: 'bid:export_bid', resumeFrom: 'make-the-pdf' }),
    {
      id: 'ask-for-a-signature',
      anchor: 'capex.estimate.signature.panel',
      route: page('signature'),
      permission: ['bid:edit_bid', 'bid:export_bid'],
      title: 'The company signs first, then the client',
      body: 'Press Ask someone to sign for the company, pick anyone and ask them. Once it is signed for the company, Send to the client emails them a link; their signature marks the estimate Awarded. On practice, nobody is asked and nothing is emailed.',
      resumeFrom: 'make-the-pdf',
      advanceOn: ADVANCE_ON.DO,
      until: { requested: 'POST /request-signature' },
      stuckHint: 'Nobody to pick? Nobody at your company holds Sign Proposals yet — an admin gives it. You can skip this step.',
    },
    openStep('open-award', 'award', page('signature'), 'Step 10 · Award',
      'Open Award.'),
    {
      id: 'budget-at-award',
      anchor: 'capex.estimate.award.standing',
      route: page('award'),
      title: 'Price, cost and the budget at award',
      body: 'Price is what the customer pays. Budget at award is what the job will be measured against once you win it: the cost, with the allowances counted at cost. GP at budget is the margin the job starts life with.',
      advanceOn: ADVANCE_ON.NEXT,
    },
    {
      id: 'start-the-award',
      anchor: 'capex.estimate.award',
      route: page('award'),
      permission: AWARD_AND_SET_UP,
      title: 'Award it',
      body: 'Awarding freezes this estimate as the job’s baseline, creates the project and loads its budget — one click, nothing retyped. Press Award & create project.',
      advanceOn: ADVANCE_ON.DO,
      until: { appears: 'capex.estimate.award.confirm' },
    },
    {
      id: 'answer-the-award',
      anchor: 'capex.estimate.award.confirm',
      screen: `${page('award')}#confirm`,
      permission: AWARD_AND_SET_UP,
      title: 'Two questions, then the job',
      // The questions open only after "Award & create project" is pressed — a resumed run presses it again.
      resumeFrom: 'start-the-award',
      body: 'Choose how the job will be billed, and how its indirects are budgeted — nothing is chosen for you. Then press Award, set up the job & open it: the estimate is frozen, the project and its budget are made, and the job opens. On practice, none of it is created.',
      advanceOn: ADVANCE_ON.DO,
      until: { requested: 'POST /award' },
      stuckHint: 'The button waits for both answers, and for a project number. If it is still grey, the line above it says what is missing.',
    },
    {
      id: 'the-project',
      anchor: 'capex.project.cost-report',
      screen: PROJECT_SCREEN,
      permission: AWARD_AND_SET_UP,
      title: 'Step 11 · The project',
      resumeFrom: 'start-the-award',
      body: 'This is the job the award made: its budget, code by code, from the frozen estimate. Cost from Finance lands here and is measured against that budget. No cost and no forecast yet — it was set up a moment ago.',
      advanceOn: ADVANCE_ON.NEXT,
    },
    {
      id: 'back-to-the-estimate',
      anchor: 'capex.project.frozen-estimate',
      screen: PROJECT_SCREEN,
      permission: AWARD_AND_SET_UP,
      title: 'The estimate it was won on',
      resumeFrom: 'start-the-award',
      body: 'Estimate (frozen at award) always opens the estimate this job was priced on — locked now, exactly as it was when you awarded it.',
      advanceOn: ADVANCE_ON.NEXT,
    },
    {
      id: 'thats-a-capital-bid',
      anchor: 'capex.nav',
      screen: `${page('award')}#confirm`,
      title: 'That is a capital bid, start to finish',
      body: 'Scope, the bill, crews, schedule, indirects, price, proposal, signature, award — and the project it becomes. Next time, start from Pipeline. Nothing you did was saved: there is no practice estimate, PDF, signature request or project anywhere. After the award, the job’s own pages have a “Tour this page” button: the job summary’s starts with where the job is — Awarded, Execute, Closeout, Closed.',
      advanceOn: ADVANCE_ON.NEXT,
    },
  ],
})
